import React, { useCallback, useContext, useMemo, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, Pencil, Plus, RefreshCw } from "lucide-react";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import { AppContext } from "../../../../../context/AppContext";
import usePermissions from "../../../../../hooks/usePermissions";
import Loading from "../../../../../utils/Loading";
import { EDSButton } from "../../../../eds/EDSButton";
import { Button } from "../../../../Shared/Buttons/Button/Button";
import { AlertBanner } from "@/components/blocks/alert-banner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "../../../../ui/dialog";
import { TaxRate } from "../../../../../types/invoices";
import {
  getLatestInvoice,
  getPreviousRevisions,
  getTypeTaxAssignments,
  getPreorderTypes,
  getTaxRegionAvailableRates
} from "../../../../../APIs/Booking/InvoiceOperations";
import { getCurrencySymbol } from "../../../../../utils/number";
import InvoiceEditor from "./InvoiceEditor";
import InvoiceViewModal from "./InvoiceViewModal";
import PreviousRevisions from "./PreviousRevisions";
import {
  canManageInvoices,
  isVenueMigrated,
  wouldInvoiceHaveItems
} from "./invoiceUtils";
import { VenueGroupConfig } from "../../../../../types/VenueGroup";
import useInvoiceState from "./useInvoiceState";
import useInvoiceActions from "./useInvoiceActions";
import useInvoiceModals from "./useInvoiceModals";

/**
 * Action buttons shown when an existing invoice is being viewed or edited.
 * Extracted to reduce cognitive complexity in the parent InvoicesSection.
 */
const ExistingInvoiceActions = ({
  isEditing,
  isSaving,
  canUpdateInvoices,
  bookingHasInvoiceableItems,
  venueMigrated,
  isGenerating,
  invoiceIsDirty,
  invoiceId,
  onSave,
  onCancel,
  onEdit,
  onGenerate,
  onShowViewModal
}: {
  isEditing: boolean;
  isSaving: boolean;
  canUpdateInvoices: boolean;
  bookingHasInvoiceableItems: boolean;
  venueMigrated: boolean;
  isGenerating: boolean;
  invoiceIsDirty: boolean;
  invoiceId: string | undefined;
  onSave: () => void;
  onCancel: () => void;
  onEdit: () => void;
  onGenerate: () => void;
  onShowViewModal: () => void;
}): React.JSX.Element => {
  if (isEditing) {
    return (
      <div className="flex gap-2 flex-wrap">
        <Button
          onClick={onSave}
          disabled={isSaving}
          fill="solid"
          size="md"
          type="primary"
        >
          {isSaving ? "Saving..." : "Save Invoice"}
        </Button>
        <Button
          onClick={onCancel}
          disabled={isSaving}
          fill="outline"
          size="md"
          type="default"
        >
          Cancel
        </Button>
      </div>
    );
  }

  return (
    <div className="flex gap-2 flex-wrap">
      {canUpdateInvoices && (
        <EDSButton
          variant="outline"
          size="sm"
          onClick={onEdit}
          aria-label="Edit invoice"
        >
          <Pencil className="w-4 h-4" />
          Edit
        </EDSButton>
      )}

      {canUpdateInvoices && bookingHasInvoiceableItems && venueMigrated && (
        <EDSButton
          variant="outline"
          size="sm"
          onClick={onGenerate}
          disabled={isGenerating || invoiceIsDirty}
          aria-label="Refresh invoice"
        >
          <RefreshCw
            className={`w-4 h-4 ${isGenerating ? "animate-spin" : ""}`}
          />
          {isGenerating ? "Refreshing..." : "Refresh Invoice"}
        </EDSButton>
      )}

      {invoiceId && !invoiceIsDirty && (
        <EDSButton
          variant="default"
          size="sm"
          onClick={onShowViewModal}
          aria-label="View invoice options"
        >
          <FileText className="w-4 h-4" />
          Invoice Options
        </EDSButton>
      )}
    </div>
  );
};

const InvoicesSection = (): React.JSX.Element => {
  const diaryContext = useDiaryContext();
  const { venueGroupConfig } = useContext(AppContext);
  usePermissions();
  const queryClient = useQueryClient();
  const booking = diaryContext.selectedBookingV4;
  const bookingId = booking?._id ?? "";

  // Venue-level config for invoice label overrides
  const venueConfig = diaryContext.venueConfig;

  // Permissions
  // Permission checks deferred until invoicing roles are available in the
  // permissions context and QA-verified end-to-end. See AB#2089613.
  // const canReadInvoices = roles.booking.invoicing.read;
  // const canUpdateInvoices = roles.booking.invoicing.update;
  const canReadInvoices = true;
  const canUpdateInvoices = true;

  // Resolve company information: venue-level override takes precedence over
  // venue group, matching collins-ui's getVenueBookingCompanyInformation
  const companyInformation = venueConfig?.override_booking_company_information
    ? (venueConfig.booking_company_information ?? undefined)
    : ((venueGroupConfig as VenueGroupConfig | undefined)
        ?.booking_company_information ?? undefined);

  // Check if venue has complete company info for invoicing
  const venueCanManageInvoices = canManageInvoices(companyInformation);

  // Tax config from booking or venue group
  const invoiceTaxConfig = booking?.invoice_tax_config;
  const vatLabel = invoiceTaxConfig?.terminology?.vat_label ?? "VAT";

  // Feature flags
  const collinsFeatures = (venueGroupConfig as VenueGroupConfig | undefined)
    ?.collins_features;
  // Bolt-on flags treated as always enabled (no disabled code paths)
  const invoiceTaxLocking = collinsFeatures?.enable_invoice_tax_locking ?? true;
  const individualItemsEnabled =
    collinsFeatures?.enable_individual_itms_invoice ?? true;

  // Currency
  const currency = booking?.deposits?.[0]?.currency ?? "GBP";
  const currencySymbol = getCurrencySymbol(currency);

  // Venue group ID for fetching type-tax-assignments and preorder types
  const venueGroupId = booking?.venue_group ?? "";

  // Tax region ID from the venue config (determines which tax rates apply)
  const taxRegionId = diaryContext.venueConfig?.tax_region_id ?? null;

  // Fetch type-tax-assignments from the API (fallback rates for new bookings)
  const { data: typeTaxAssignments } = useQuery({
    queryKey: ["typeTaxAssignments", venueGroupId, taxRegionId],
    queryFn: () => getTypeTaxAssignments(venueGroupId, taxRegionId),
    enabled: !!venueGroupId
  });

  // Fetch preorder types (typesCache) for type name resolution and
  // service charge / deposits type lookup
  const { data: typesCache } = useQuery({
    queryKey: ["preorderTypes", venueGroupId],
    queryFn: () => getPreorderTypes(venueGroupId),
    enabled: !!venueGroupId
  });

  // Fetch available tax rates for the venue's tax region.
  // Matches collins-ui's VenuePreordersProvider.getTaxRegionAvailableRates
  // which populates taxConfig.regionTaxRates for the invoice dropdown.
  const { data: regionTaxRates } = useQuery({
    queryKey: ["regionTaxRates", venueGroupId, taxRegionId],
    queryFn: () => getTaxRegionAvailableRates(venueGroupId, taxRegionId!),
    enabled: !!venueGroupId && !!taxRegionId
  });

  // Compute the selectable tax rates for the invoice item dropdown.
  // Mirrors collins-ui's getTaxRates() in invoicesSection.js:
  //   1. Preferred: regionTaxRates from the tax-region available-rates API
  //   2. Fallback: booking's invoice_tax_config.tax_rates (locked rates)
  const taxRates: TaxRate[] = useMemo(() => {
    if (Array.isArray(regionTaxRates) && regionTaxRates.length > 0) {
      return regionTaxRates.map((rate) => ({
        _id: rate._id,
        label: rate.name,
        value: rate.rate,
        rate: rate.rate,
        name: rate.name
      }));
    }
    return invoiceTaxConfig?.tax_rates ?? [];
  }, [regionTaxRates, invoiceTaxConfig]);

  // Venue packages for resolving type_id on preorder items
  const venuePackages = (venueGroupConfig as VenueGroupConfig | undefined)
    ?.booking_packages;

  // Determine if venue is migrated to the new tax system
  const venueMigrated = isVenueMigrated(
    typeTaxAssignments,
    typesCache,
    taxRegionId
  );

  // Fetch latest invoice
  const {
    data: latestInvoice,
    isLoading: isLoadingInvoice,
    refetch: refetchInvoice
  } = useQuery({
    queryKey: ["latestInvoice", bookingId],
    queryFn: () => getLatestInvoice(bookingId),
    enabled: !!bookingId && canReadInvoices
  });

  // Fetch previous revisions
  const {
    data: revisions,
    isLoading: isLoadingRevisions,
    refetch: refetchRevisions
  } = useQuery({
    queryKey: ["invoiceRevisions", bookingId],
    queryFn: () => getPreviousRevisions(bookingId),
    enabled: !!bookingId && canReadInvoices
  });

  // --- Custom hooks for state, actions, and modals ---

  // Use a ref to hold the clearSaveError callback so useInvoiceState can
  // call it without a circular dependency on useInvoiceActions.
  const clearSaveErrorRef = useRef<() => void>(() => {});
  const stableClearSaveError = useCallback(() => {
    clearSaveErrorRef.current();
  }, []);

  const {
    showViewModal,
    setShowViewModal,
    showDeleteConfirm,
    setShowDeleteConfirm,
    revisionPreviewId,
    setRevisionPreviewId,
    handleViewRevision,
    handlePrintRevision
  } = useInvoiceModals();

  const {
    invoice,
    setInvoice,
    isEditing,
    setIsEditing,
    invoiceIsDirty,
    showDiscardConfirm,
    setShowDiscardConfirm,
    handleUpdateInvoice,
    handleUpdateItem,
    handleRemoveItem,
    handleAddBlankItem,
    handleAddDepositItems,
    handleAddPreorderItems,
    handleAddInvoice,
    handleEditInvoice,
    handleCancelEdit,
    confirmCancelEdit
  } = useInvoiceState({
    booking,
    latestInvoice: latestInvoice ?? undefined,
    taxRates,
    venueConfig: venueConfig ?? undefined,
    venueGroupConfig: venueGroupConfig as {
      override_booking_invoicing?: boolean;
      booking_invoicing?: Record<string, string>;
    },
    typesCache,
    invoiceTaxConfig,
    typeTaxAssignments,
    invoiceTaxLocking,
    individualItemsEnabled,
    onClearSaveError: stableClearSaveError,
    venuePackages,
    venueMigrated
  });

  const {
    isSaving,
    isDeleting,
    isGenerating,
    isPrinting,
    isDownloading,
    isDownloadingCsv,
    isEmailing,
    saveError,
    setSaveError,
    deleteError,
    generateError,
    generateSuccess,
    emailSuccess,
    actionError,
    setActionError,
    handleSaveInvoice,
    handleDeleteInvoice,
    handleGenerateViaApi,
    handlePrint,
    handleDownload,
    handleDownloadCsv,
    handleEmail
  } = useInvoiceActions({
    invoice,
    setInvoice,
    setIsEditing,
    bookingId,
    booking: booking ?? null,
    diaryContext: {
      setDiaryContext: diaryContext.setDiaryContext as (
        updates: Record<string, unknown>
      ) => void
    },
    refetchInvoice,
    refetchRevisions,
    queryClient,
    setShowDeleteConfirm,
    handleAddInvoice,
    handleAddPreorderItems
  });

  // Wire up the ref now that setSaveError is available
  clearSaveErrorRef.current = () => setSaveError(null);

  // Loading state
  if (!booking) {
    return (
      <div className="flex flex-col gap-3 rounded-lg bg-[var(--panelBackground)] p-[var(--panelPadding)] shadow-sm" />
    );
  }

  if (!canReadInvoices) {
    return (
      <div className="flex flex-col gap-3 rounded-lg bg-[var(--panelBackground)] p-[var(--panelPadding)] shadow-sm">
        <div className="text-center text-sm text-muted-foreground p-4">
          You do not have permission to view invoices.
        </div>
      </div>
    );
  }

  // Gate invoice management if venue is missing company information
  if (!venueCanManageInvoices) {
    return (
      <div className="flex flex-col gap-3 rounded-lg bg-[var(--panelBackground)] p-[var(--panelPadding)] shadow-sm">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-base">Invoices</h3>
        </div>
        <div className="text-center text-sm text-muted-foreground p-4">
          Invoice management requires venue company name, registered address,
          and VAT number to be configured.
        </div>
      </div>
    );
  }

  const isLoading = isLoadingInvoice || isLoadingRevisions;
  const hasInvoice = invoice !== null;
  const bookingHasInvoiceableItems = booking
    ? wouldInvoiceHaveItems(booking)
    : false;
  const previousRevisions = (revisions ?? []).filter(
    (rev) => rev.id !== invoice?.id
  );

  return (
    <div className="flex flex-col gap-3 rounded-lg bg-[var(--panelBackground)] p-[var(--panelPadding)] shadow-sm">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-base">Invoices</h3>
      </div>

      {isLoading && <Loading />}

      {!isLoading && (
        <>
          {generateSuccess && (
            <AlertBanner
              variant="success"
              title="Invoice generated successfully"
              dismissible
              className="mb-3"
            />
          )}

          {generateError && (
            <AlertBanner
              variant="error"
              title={generateError}
              dismissible
              className="mb-3"
            />
          )}

          {emailSuccess && (
            <AlertBanner
              variant="success"
              title="Invoice email sent successfully"
              dismissible
              className="mb-3"
            />
          )}

          {previousRevisions.length > 0 && (
            <PreviousRevisions
              revisions={previousRevisions}
              currencySymbol={currencySymbol}
              onViewRevision={handleViewRevision}
              onPrintRevision={handlePrintRevision}
            />
          )}

          {hasInvoice && (
            <div className="flex flex-col">
              {invoice.ref && (
                <div className="text-sm text-muted-foreground mb-2">
                  Reference: <span className="font-medium">{invoice.ref}</span>
                </div>
              )}

              <InvoiceEditor
                invoice={invoice}
                booking={booking}
                taxRates={taxRates}
                vatLabel={vatLabel}
                currencySymbol={currencySymbol}
                isEditing={isEditing}
                onUpdateInvoice={handleUpdateInvoice}
                onUpdateItem={handleUpdateItem}
                onRemoveItem={handleRemoveItem}
                onAddBlankItem={handleAddBlankItem}
              />

              {saveError && (
                <AlertBanner
                  variant="error"
                  title={saveError}
                  dismissible
                  className="mt-3"
                />
              )}

              {deleteError && (
                <AlertBanner
                  variant="error"
                  title={deleteError}
                  dismissible
                  className="mt-3"
                />
              )}

              {actionError && (
                <AlertBanner
                  variant="error"
                  title={actionError}
                  dismissible
                  onDismiss={() => setActionError(null)}
                  className="mt-3"
                />
              )}

              <div className="flex gap-2 flex-wrap mt-4">
                <ExistingInvoiceActions
                  isEditing={isEditing}
                  isSaving={isSaving}
                  canUpdateInvoices={canUpdateInvoices}
                  bookingHasInvoiceableItems={bookingHasInvoiceableItems}
                  venueMigrated={venueMigrated}
                  isGenerating={isGenerating}
                  invoiceIsDirty={invoiceIsDirty}
                  invoiceId={invoice.id}
                  onSave={handleSaveInvoice}
                  onCancel={handleCancelEdit}
                  onEdit={handleEditInvoice}
                  onGenerate={handleGenerateViaApi}
                  onShowViewModal={() => setShowViewModal(true)}
                />
              </div>
            </div>
          )}

          {!hasInvoice && !isLoading && (
            <div className="flex flex-col items-center p-[var(--panelPadding)]">
              <p className="text-sm text-muted-foreground mb-3">
                No invoice has been created for this booking.
              </p>
              {canUpdateInvoices && (
                <div className="flex gap-2 flex-wrap">
                  <Button
                    onClick={handleAddInvoice}
                    type="primary"
                    fill="solid"
                    size="md"
                  >
                    <span className="flex items-center gap-1">
                      <Plus className="w-4 h-4" />
                      Add Invoice
                    </span>
                  </Button>

                  {(booking?.preorders?.length ?? 0) > 0 && (
                    <EDSButton
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        handleAddInvoice();
                        handleAddPreorderItems();
                        handleAddDepositItems();
                      }}
                      aria-label="Add invoice from pre-orders"
                    >
                      <Plus className="w-4 h-4" />
                      Add from Pre-orders
                    </EDSButton>
                  )}

                  {(booking?.deposits?.length ?? 0) > 0 && (
                    <EDSButton
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        handleAddInvoice();
                        handleAddDepositItems();
                      }}
                      aria-label="Add invoice from deposits"
                    >
                      <Plus className="w-4 h-4" />
                      Add from Deposits
                    </EDSButton>
                  )}

                  {bookingHasInvoiceableItems && venueMigrated && (
                    <EDSButton
                      variant="outline"
                      size="sm"
                      onClick={handleGenerateViaApi}
                      disabled={isGenerating}
                      aria-label="Auto-generate invoice"
                    >
                      <RefreshCw
                        className={`w-4 h-4 ${isGenerating ? "animate-spin" : ""}`}
                      />
                      {isGenerating ? "Generating..." : "Auto-generate"}
                    </EDSButton>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {showViewModal && invoice?.id && (
        <InvoiceViewModal
          bookingId={bookingId}
          invoiceId={invoice.id}
          onClose={() => setShowViewModal(false)}
          onPrint={handlePrint}
          onDownload={handleDownload}
          onDownloadCsv={handleDownloadCsv}
          onEmail={handleEmail}
          isPrinting={isPrinting}
          isDownloading={isDownloading}
          isDownloadingCsv={isDownloadingCsv}
          isEmailing={isEmailing}
        />
      )}

      {revisionPreviewId && (
        <InvoiceViewModal
          bookingId={bookingId}
          invoiceId={revisionPreviewId}
          onClose={() => setRevisionPreviewId(null)}
          onPrint={handlePrint}
          onDownload={handleDownload}
          onDownloadCsv={handleDownloadCsv}
          onEmail={handleEmail}
          isPrinting={isPrinting}
          isDownloading={isDownloading}
          isDownloadingCsv={isDownloadingCsv}
          isEmailing={isEmailing}
        />
      )}

      <Dialog
        open={showDeleteConfirm}
        onOpenChange={(open) => !isDeleting && setShowDeleteConfirm(open)}
      >
        <DialogContent
          showCloseButton={false}
          className="bg-[var(--panelBackground)] shadow-[0_3px_16px_0_rgba(0,0,0,0.16)] max-w-[400px] w-[90%]"
          aria-describedby="delete-invoice-description"
        >
          <DialogHeader>
            <DialogTitle>Delete invoice</DialogTitle>
            <DialogDescription id="delete-invoice-description">
              Are you sure you want to delete this invoice? This action cannot
              be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <EDSButton
              variant="outline"
              size="sm"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={isDeleting}
              autoFocus
            >
              Cancel
            </EDSButton>
            <EDSButton
              variant="destructive"
              size="sm"
              onClick={handleDeleteInvoice}
              disabled={isDeleting}
              aria-label="Confirm delete invoice"
            >
              {isDeleting ? "Deleting..." : "Delete Invoice"}
            </EDSButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDiscardConfirm} onOpenChange={setShowDiscardConfirm}>
        <DialogContent
          showCloseButton={false}
          className="bg-[var(--panelBackground)] shadow-[0_3px_16px_0_rgba(0,0,0,0.16)] max-w-[400px] w-[90%]"
          aria-describedby="discard-changes-description"
        >
          <DialogHeader>
            <DialogTitle>Discard changes</DialogTitle>
            <DialogDescription id="discard-changes-description">
              You have unsaved changes. Are you sure you want to discard them?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <EDSButton
              variant="outline"
              size="sm"
              onClick={() => setShowDiscardConfirm(false)}
              autoFocus
            >
              Keep editing
            </EDSButton>
            <EDSButton
              variant="destructive"
              size="sm"
              onClick={confirmCancelEdit}
              aria-label="Confirm discard changes"
            >
              Discard changes
            </EDSButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoicesSection;
